import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Star, ExternalLink, Mail, Phone, MapPin, Users, Heart, StickyNote, Activity } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { DuplicateWithDetails, DeduplicatedEmail, DeduplicatedPhone, DeduplicatedAddress } from '@/types/duplicate';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { captureError } from '@/lib/sentry';

interface MergeFormProps {
  duplicate: DuplicateWithDetails;
  onClose: () => void;
  onSuccess: (mergedContactId?: string) => void;
}

export function MergeForm({ duplicate, onClose, onSuccess }: MergeFormProps) {
  // Base settings - start with no selection to require explicit user choice
  const [keepId, setKeepId] = useState<string>('');
  const [primaryEmail, setPrimaryEmail] = useState<string>('');
  const [primaryPhone, setPrimaryPhone] = useState<string>('');
  const [primaryAddress, setPrimaryAddress] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Advanced selection options
  const [keepEmails, setKeepEmails] = useState<Record<string, boolean>>({});
  const [keepPhones, setKeepPhones] = useState<Record<string, boolean>>({});
  const [keepAddresses, setKeepAddresses] = useState<Record<string, boolean>>({});
  const { toast } = useToast();

  const contact1 = duplicate.contact1;
  const contact2 = duplicate.contact2;

  // Helper function to get field values from contacts
  const getFieldValue = (contact: any, fieldName: string) => {
    if (!contact || typeof contact !== 'object') {
      return '-';
    }

    // Handle nested fields like 'custom_fields.field_name'
    if (fieldName.includes('.')) {
      const [parent, child] = fieldName.split('.', 2);
      return contact[parent] && contact[parent][child] ? contact[parent][child] : '-';
    }

    // Handle direct fields
    return contact[fieldName] || '-';
  };

  // Deduplicate emails: shared emails shown once with 'shared' source
  const deduplicatedEmails = useMemo((): DeduplicatedEmail[] => {
    const emailMap = new Map<string, DeduplicatedEmail>();

    for (const e of duplicate.emails1) {
      const key = e.email.toLowerCase();
      emailMap.set(key, {
        email: e.email,
        is_primary: e.is_primary,
        source: 'contact1',
        contact_id: e.contact_id,
      });
    }

    for (const e of duplicate.emails2) {
      const key = e.email.toLowerCase();
      const existing = emailMap.get(key);
      if (existing) {
        // Shared email - mark as shared, keep primary if either has it
        existing.source = 'shared';
        if (e.is_primary) existing.is_primary = true;
      } else {
        emailMap.set(key, {
          email: e.email,
          is_primary: e.is_primary,
          source: 'contact2',
          contact_id: e.contact_id,
        });
      }
    }

    return Array.from(emailMap.values());
  }, [duplicate.emails1, duplicate.emails2]);

  // Deduplicate phones: shared phones shown once with 'shared' source
  const deduplicatedPhones = useMemo((): DeduplicatedPhone[] => {
    const phoneMap = new Map<string, DeduplicatedPhone>();

    // Normalize phone for comparison: strip non-digits
    const normalize = (phone: string) => phone.replace(/\D/g, '');

    for (const p of duplicate.phones1) {
      const key = normalize(p.phone_number);
      phoneMap.set(key, {
        phone_number: p.phone_number,
        isPrimary: p.isPrimary,
        label: p.label,
        source: 'contact1',
        contact_id: p.contact_id,
      });
    }

    for (const p of duplicate.phones2) {
      const key = normalize(p.phone_number);
      const existing = phoneMap.get(key);
      if (existing) {
        existing.source = 'shared';
        if (p.isPrimary) existing.isPrimary = true;
        if (!existing.label && p.label) existing.label = p.label;
      } else {
        phoneMap.set(key, {
          phone_number: p.phone_number,
          isPrimary: p.isPrimary,
          label: p.label,
          source: 'contact2',
          contact_id: p.contact_id,
        });
      }
    }

    return Array.from(phoneMap.values());
  }, [duplicate.phones1, duplicate.phones2]);

  // Deduplicate addresses: same street+city+state+zip shown once with 'shared' source
  const deduplicatedAddresses = useMemo((): DeduplicatedAddress[] => {
    const addressMap = new Map<string, DeduplicatedAddress>();

    const normalizeAddr = (a: { address_line1: string; city: string; state?: string; postal_code?: string }) =>
      `${a.address_line1}|${a.city}|${a.state || ''}|${a.postal_code || ''}`.toLowerCase().trim();

    for (const a of (duplicate.addresses1 || [])) {
      const key = normalizeAddr(a);
      addressMap.set(key, {
        id: a.id,
        address_line1: a.address_line1,
        address_line2: a.address_line2,
        city: a.city,
        state: a.state,
        postal_code: a.postal_code,
        country: a.country,
        label: a.label,
        is_primary: a.is_primary,
        source: 'contact1',
        contact_id: a.contact_id,
      });
    }

    for (const a of (duplicate.addresses2 || [])) {
      const key = normalizeAddr(a);
      const existing = addressMap.get(key);
      if (existing) {
        existing.source = 'shared';
        if (a.is_primary) existing.is_primary = true;
      } else {
        addressMap.set(key, {
          id: a.id,
          address_line1: a.address_line1,
          address_line2: a.address_line2,
          city: a.city,
          state: a.state,
          postal_code: a.postal_code,
          country: a.country,
          label: a.label,
          is_primary: a.is_primary,
          source: 'contact2',
          contact_id: a.contact_id,
        });
      }
    }

    return Array.from(addressMap.values());
  }, [duplicate.addresses1, duplicate.addresses2]);

  // Set default primary values and selections on mount
  useEffect(() => {
    // Default to already primary email
    const primaryEmail1 = duplicate.emails1.find(e => e.is_primary);
    const primaryEmail2 = duplicate.emails2.find(e => e.is_primary);

    if (primaryEmail1) {
      setPrimaryEmail(primaryEmail1.email);
    } else if (primaryEmail2) {
      setPrimaryEmail(primaryEmail2.email);
    } else if (duplicate.emails1.length > 0) {
      setPrimaryEmail(duplicate.emails1[0].email);
    } else if (duplicate.emails2.length > 0) {
      setPrimaryEmail(duplicate.emails2[0].email);
    }

    // Default to already primary phone
    const primaryPhone1 = duplicate.phones1.find(p => p.isPrimary);
    const primaryPhone2 = duplicate.phones2.find(p => p.isPrimary);

    if (primaryPhone1) {
      setPrimaryPhone(primaryPhone1.phone_number);
    } else if (primaryPhone2) {
      setPrimaryPhone(primaryPhone2.phone_number);
    } else if (duplicate.phones1.length > 0) {
      setPrimaryPhone(duplicate.phones1[0].phone_number);
    } else if (duplicate.phones2.length > 0) {
      setPrimaryPhone(duplicate.phones2[0].phone_number);
    }

    // Default to already primary address if available
    const primaryAddress1 = duplicate.addresses1?.find(a => a.is_primary);
    const primaryAddress2 = duplicate.addresses2?.find(a => a.is_primary);

    if (primaryAddress1) {
      setPrimaryAddress(primaryAddress1.id);
    } else if (primaryAddress2) {
      setPrimaryAddress(primaryAddress2.id);
    } else if (duplicate.addresses1?.length && duplicate.addresses1.length > 0) {
      setPrimaryAddress(duplicate.addresses1[0]?.id || '');
    } else if (duplicate.addresses2?.length && duplicate.addresses2.length > 0) {
      setPrimaryAddress(duplicate.addresses2[0]?.id || '');
    }

    // Set all deduplicated emails to be kept by default
    const emailDefaults: Record<string, boolean> = {};
    deduplicatedEmails.forEach(email => {
      emailDefaults[email.email] = true;
    });
    setKeepEmails(emailDefaults);

    // Set all deduplicated phones to be kept by default
    const phoneDefaults: Record<string, boolean> = {};
    deduplicatedPhones.forEach(phone => {
      phoneDefaults[phone.phone_number] = true;
    });
    setKeepPhones(phoneDefaults);

    // Set all deduplicated addresses to be kept by default
    const addressDefaults: Record<string, boolean> = {};
    deduplicatedAddresses.forEach(address => {
      addressDefaults[address.id] = true;
    });
    setKeepAddresses(addressDefaults);
  }, [duplicate, deduplicatedEmails, deduplicatedPhones, deduplicatedAddresses]);

  const handleSubmit = async () => {
    if (!keepId) {
      toast({
        title: "Error",
        description: "Please select which contact to keep",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const discardId = keepId === duplicate.duplicate.contact1_id
        ? duplicate.duplicate.contact2_id
        : duplicate.duplicate.contact1_id;

      const selectedEmails = Object.entries(keepEmails)
        .filter(([, keep]) => keep)
        .map(([email]) => email);

      const selectedPhones = Object.entries(keepPhones)
        .filter(([, keep]) => keep)
        .map(([phone]) => phone);

      // Call the merge RPC which respects user selections
      const { error } = await supabase.rpc('merge_contacts_v2', {
        keep_id: keepId,
        discard_id: discardId,
        primary_email: primaryEmail || null,
        primary_phone: primaryPhone || null,
        options: {
          keep_emails: selectedEmails,
          keep_phones: selectedPhones,
        },
      });
      if (error) throw error;

      onSuccess(keepId);
      onClose();
    } catch (error: any) {
      console.error('Error merging contacts:', error);
      captureError(error as Error, { feature: 'duplicates-merge-submit' });
      toast({
        title: "Merge Failed",
        description: error?.message || "There was a problem merging the contacts",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle primary email: click to set, click again to unset
  const handleSelectPrimaryEmail = (email: string) => {
    setPrimaryEmail(prev => prev === email ? '' : email);
  };

  // Toggle primary phone: click to set, click again to unset
  const handleSelectPrimaryPhone = (phoneNumber: string) => {
    setPrimaryPhone(prev => prev === phoneNumber ? '' : phoneNumber);
  };

  const handleSelectPrimaryAddress = (addressId: string) => {
    setPrimaryAddress(prev => prev === addressId ? '' : addressId);
  };

  // Format address for display
  const formatAddress = (address: {
    address_line1: string;
    address_line2?: string;
    city: string;
    state?: string;
    postal_code?: string;
    country: string;
  }) => {
    const parts = [
      address.address_line1,
      address.address_line2,
      [address.city, address.state, address.postal_code].filter(Boolean).join(', '),
      address.country
    ].filter(Boolean);

    return parts.join(', ');
  };

  // Open contact profile in new tab
  const openContactProfile = (contactId: string) => {
    window.open(`/contacts/${contactId}`, '_blank');
  };

  // Source badge helper
  const sourceBadge = (source: 'shared' | 'contact1' | 'contact2') => {
    if (source === 'shared') {
      return <Badge variant="outline" className="text-xs border-green-300 text-green-700 bg-green-50">Shared</Badge>;
    }
    return (
      <Badge variant="secondary" className="text-xs">
        {source === 'contact1' ? 'Contact 1' : 'Contact 2'}
      </Badge>
    );
  };

  // Compute merge preview data
  const keptEmailCount = Object.values(keepEmails).filter(Boolean).length;
  const keptPhoneCount = Object.values(keepPhones).filter(Boolean).length;
  const keptAddressCount = Object.values(keepAddresses).filter(Boolean).length;
  const keepContact = keepId === contact1.id ? contact1 : keepId === contact2.id ? contact2 : null;

  return (
    <TooltipProvider>
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <input
            type="text"
            style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
            autoFocus
          />
          <DialogHeader>
            <DialogTitle>Merge Contacts</DialogTitle>
            <DialogDescription>
              Select which contact to keep and configure what data to include in the merged contact.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[calc(90vh-12rem)]">
            <div className="py-4 space-y-6">
              {/* Contact Selection */}
              <div className="space-y-4">
                <h3 className="text-base font-medium">1. Select Contact to Keep</h3>
                <RadioGroup
                  value={keepId}
                  onValueChange={setKeepId}
                  className="flex gap-4"
                >
                  <div className="flex-1 border p-4 rounded-lg">
                    <div className="flex items-start gap-2">
                      <RadioGroupItem
                        value={contact1.id}
                        id="contact1"
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="contact1" className="text-base cursor-pointer">
                            {contact1.is_company
                              ? contact1.company_name
                              : `${contact1.first_name} ${contact1.last_name}`}
                          </Label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openContactProfile(contact1.id);
                                }}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>View full contact details</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>

                        {/* Job Info */}
                        {contact1.job_title && (
                          <p className="text-sm text-muted-foreground">{contact1.job_title}</p>
                        )}
                        {!contact1.is_company && contact1.company_name && (
                          <p className="text-sm">{contact1.company_name}</p>
                        )}
                        {contact1.industry && (
                          <p className="text-sm text-muted-foreground">Industry: {contact1.industry}</p>
                        )}

                        {/* Smart Fields */}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">Type:</span>{" "}
                            {getFieldValue(contact1, "type")}
                          </div>
                          <div>
                            <span className="text-muted-foreground">Rank:</span>{" "}
                            {getFieldValue(contact1, "rank")}
                          </div>
                          <div>
                            <span className="text-muted-foreground">Status:</span>{" "}
                            {getFieldValue(contact1, "status")}
                          </div>
                          <div>
                            <span className="text-muted-foreground">Relationship:</span>{" "}
                            {getFieldValue(contact1, "relationship")}
                          </div>
                        </div>

                        <div className="text-sm mt-2">
                          <span className="text-muted-foreground">
                            {duplicate.emails1.length} email{duplicate.emails1.length !== 1 && 's'},
                            {' '}{duplicate.phones1.length} phone{duplicate.phones1.length !== 1 && 's'},
                            {' '}{duplicate.addresses1?.length || 0} address{(duplicate.addresses1?.length || 0) !== 1 && 'es'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 border p-4 rounded-lg">
                    <div className="flex items-start gap-2">
                      <RadioGroupItem
                        value={contact2.id}
                        id="contact2"
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="contact2" className="text-base cursor-pointer">
                            {contact2.is_company
                              ? contact2.company_name
                              : `${contact2.first_name} ${contact2.last_name}`}
                          </Label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openContactProfile(contact2.id);
                                }}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>View full contact details</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>

                        {/* Job Info */}
                        {contact2.job_title && (
                          <p className="text-sm text-muted-foreground">{contact2.job_title}</p>
                        )}
                        {!contact2.is_company && contact2.company_name && (
                          <p className="text-sm">{contact2.company_name}</p>
                        )}
                        {contact2.industry && (
                          <p className="text-sm text-muted-foreground">Industry: {contact2.industry}</p>
                        )}

                        {/* Smart Fields */}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">Type:</span>{" "}
                            {getFieldValue(contact2, "type")}
                          </div>
                          <div>
                            <span className="text-muted-foreground">Rank:</span>{" "}
                            {getFieldValue(contact2, "rank")}
                          </div>
                          <div>
                            <span className="text-muted-foreground">Status:</span>{" "}
                            {getFieldValue(contact2, "status")}
                          </div>
                          <div>
                            <span className="text-muted-foreground">Relationship:</span>{" "}
                            {getFieldValue(contact2, "relationship")}
                          </div>
                        </div>

                        <div className="text-sm mt-2">
                          <span className="text-muted-foreground">
                            {duplicate.emails2.length} email{duplicate.emails2.length !== 1 && 's'},
                            {' '}{duplicate.phones2.length} phone{duplicate.phones2.length !== 1 && 's'},
                            {' '}{duplicate.addresses2?.length || 0} address{(duplicate.addresses2?.length || 0) !== 1 && 'es'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              <Accordion type="multiple" className="w-full border rounded-lg">
                {/* Email Configuration */}
                {deduplicatedEmails.length > 0 && (
                  <AccordionItem value="emails">
                    <AccordionTrigger className="px-4">Email Addresses</AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="space-y-4">
                        <h4 className="text-sm font-medium">Select Emails to Keep</h4>
                        <Card>
                          <CardContent className="p-4 space-y-2">
                            {deduplicatedEmails.map((email, idx) => (
                              <div key={`email-${idx}`} className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <Checkbox
                                    id={`email-${idx}`}
                                    checked={keepEmails[email.email] || false}
                                    onCheckedChange={(checked) => {
                                      setKeepEmails(prev => ({
                                        ...prev,
                                        [email.email]: !!checked
                                      }));
                                      // Auto-clear primary when unchecking primary item
                                      if (!checked && email.email === primaryEmail) {
                                        setPrimaryEmail('');
                                      }
                                    }}
                                  />
                                  <div className="flex-1">
                                    <Label
                                      htmlFor={`email-${idx}`}
                                      className="text-sm cursor-pointer flex items-center gap-2"
                                    >
                                      {email.email}
                                      {sourceBadge(email.source)}
                                    </Label>
                                  </div>
                                </div>

                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={cn(
                                    "h-8 w-8",
                                    email.email === primaryEmail ? "text-yellow-500" : "text-muted-foreground"
                                  )}
                                  onClick={() => handleSelectPrimaryEmail(email.email)}
                                  title={email.email === primaryEmail ? "Primary email (click to unset)" : "Set as primary"}
                                >
                                  <Star
                                    className={cn(
                                      "h-5 w-5",
                                      email.email === primaryEmail && "fill-current"
                                    )}
                                  />
                                </Button>
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* Phone Configuration */}
                {deduplicatedPhones.length > 0 && (
                  <AccordionItem value="phones">
                    <AccordionTrigger className="px-4">Phone Numbers</AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="space-y-4">
                        <h4 className="text-sm font-medium">Select Phones to Keep</h4>
                        <Card>
                          <CardContent className="p-4 space-y-2">
                            {deduplicatedPhones.map((phone, idx) => (
                              <div key={`phone-${idx}`} className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <Checkbox
                                    id={`phone-${idx}`}
                                    checked={keepPhones[phone.phone_number] || false}
                                    onCheckedChange={(checked) => {
                                      setKeepPhones(prev => ({
                                        ...prev,
                                        [phone.phone_number]: !!checked
                                      }));
                                      // Auto-clear primary when unchecking primary item
                                      if (!checked && phone.phone_number === primaryPhone) {
                                        setPrimaryPhone('');
                                      }
                                    }}
                                  />
                                  <div className="flex-1">
                                    <Label
                                      htmlFor={`phone-${idx}`}
                                      className="text-sm cursor-pointer flex items-center gap-2"
                                    >
                                      {phone.phone_number}
                                      {sourceBadge(phone.source)}
                                      {phone.label && <span className="text-xs text-muted-foreground">({phone.label})</span>}
                                    </Label>
                                  </div>
                                </div>

                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={cn(
                                    "h-8 w-8",
                                    phone.phone_number === primaryPhone ? "text-yellow-500" : "text-muted-foreground"
                                  )}
                                  onClick={() => handleSelectPrimaryPhone(phone.phone_number)}
                                  title={phone.phone_number === primaryPhone ? "Primary phone (click to unset)" : "Set as primary"}
                                >
                                  <Star
                                    className={cn(
                                      "h-5 w-5",
                                      phone.phone_number === primaryPhone && "fill-current"
                                    )}
                                  />
                                </Button>
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* Physical Addresses Configuration */}
                {deduplicatedAddresses.length > 0 && (
                  <AccordionItem value="addresses">
                    <AccordionTrigger className="px-4">Physical Addresses</AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="space-y-4">
                        <h4 className="text-sm font-medium">Select Addresses to Keep</h4>
                        <Card>
                          <CardContent className="p-4 space-y-2">
                            {deduplicatedAddresses.map((address, idx) => (
                              <div key={address.id || `address-${idx}`} className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <Checkbox
                                    id={`address-${idx}`}
                                    checked={keepAddresses[address.id] || false}
                                    onCheckedChange={(checked) => {
                                      setKeepAddresses(prev => ({
                                        ...prev,
                                        [address.id]: !!checked
                                      }));
                                      if (!checked && address.id === primaryAddress) {
                                        setPrimaryAddress('');
                                      }
                                    }}
                                  />
                                  <div className="flex-1">
                                    <Label
                                      htmlFor={`address-${idx}`}
                                      className="text-sm cursor-pointer flex items-center gap-2"
                                    >
                                      <div>
                                        <div>{formatAddress(address)}</div>
                                        <div className="flex items-center gap-1 mt-1">
                                          {sourceBadge(address.source)}
                                          {address.label && <span className="text-xs text-muted-foreground">({address.label})</span>}
                                        </div>
                                      </div>
                                    </Label>
                                  </div>
                                </div>

                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={cn(
                                    "h-8 w-8",
                                    address.id === primaryAddress ? "text-yellow-500" : "text-muted-foreground"
                                  )}
                                  onClick={() => handleSelectPrimaryAddress(address.id)}
                                  title={address.id === primaryAddress ? "Primary address (click to unset)" : "Set as primary"}
                                >
                                  <Star
                                    className={cn(
                                      "h-5 w-5",
                                      address.id === primaryAddress && "fill-current"
                                    )}
                                  />
                                </Button>
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* Additional Contact Data */}
                <AccordionItem value="additional">
                  <AccordionTrigger className="px-4">Additional Data</AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground mb-3">
                        The following data from both contacts will be automatically combined:
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-start gap-2 p-3 rounded-md border bg-muted/30">
                          <Users className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                          <div>
                            <p className="text-sm font-medium">Groups</p>
                            <p className="text-xs text-muted-foreground">All group memberships will be combined</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 p-3 rounded-md border bg-muted/30">
                          <Heart className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                          <div>
                            <p className="text-sm font-medium">Interests</p>
                            <p className="text-xs text-muted-foreground">All interests will be combined</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 p-3 rounded-md border bg-muted/30">
                          <StickyNote className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                          <div>
                            <p className="text-sm font-medium">Notes</p>
                            <p className="text-xs text-muted-foreground">All notes will be preserved</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 p-3 rounded-md border bg-muted/30">
                          <Activity className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                          <div>
                            <p className="text-sm font-medium">Activities</p>
                            <p className="text-xs text-muted-foreground">Activity history will be preserved</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* Merge Preview */}
              {keepContact && (
                <Card className="border-primary/30 bg-primary/5">
                  <CardContent className="p-4">
                    <h4 className="text-sm font-medium mb-3">Merge Preview</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {keepContact.is_company
                            ? keepContact.company_name
                            : `${keepContact.first_name} ${keepContact.last_name}`}
                        </span>
                      </div>

                      {keptEmailCount > 0 && (
                        <div className="flex items-start gap-2">
                          <Mail className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                          <div className="flex flex-wrap gap-1">
                            {deduplicatedEmails
                              .filter(e => keepEmails[e.email])
                              .map(e => (
                                <span key={e.email} className="inline-flex items-center gap-1">
                                  {e.email}
                                  {e.email === primaryEmail && (
                                    <Star className="h-3 w-3 text-yellow-500 fill-current" />
                                  )}
                                  {e.email !== deduplicatedEmails.filter(em => keepEmails[em.email]).slice(-1)[0]?.email && (
                                    <span className="text-muted-foreground">,</span>
                                  )}
                                </span>
                              ))}
                          </div>
                        </div>
                      )}

                      {keptPhoneCount > 0 && (
                        <div className="flex items-start gap-2">
                          <Phone className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                          <div className="flex flex-wrap gap-1">
                            {deduplicatedPhones
                              .filter(p => keepPhones[p.phone_number])
                              .map(p => (
                                <span key={p.phone_number} className="inline-flex items-center gap-1">
                                  {p.phone_number}
                                  {p.phone_number === primaryPhone && (
                                    <Star className="h-3 w-3 text-yellow-500 fill-current" />
                                  )}
                                  {p.phone_number !== deduplicatedPhones.filter(ph => keepPhones[ph.phone_number]).slice(-1)[0]?.phone_number && (
                                    <span className="text-muted-foreground">,</span>
                                  )}
                                </span>
                              ))}
                          </div>
                        </div>
                      )}

                      {keptAddressCount > 0 && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <span className="text-muted-foreground">{keptAddressCount} address{keptAddressCount !== 1 && 'es'}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </ScrollArea>

          <DialogFooter className="mt-4 pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting || !keepId}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Merging...
                </>
              ) : !keepId ? (
                'Select a contact to keep'
              ) : (
                'Merge Contacts'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
